/**
 * Pending-prompt handoff for Ask Flint.
 *
 * A surface that collects a question *outside* the chat (the Ask Flint
 * home composer, the launcher omnibox) can't send it directly — the
 * `useChat` transport only exists once `/ask/$chatId` mounts. The
 * handoff contract: mint a chat id, stash the prompt in sessionStorage
 * under `flint-ai-pending-<chatId>`, navigate to the chat route, and
 * `ChatShell` reads + clears the key on first mount and auto-submits.
 *
 * These helpers own that key convention so no caller spells the string.
 * sessionStorage (not localStorage) on purpose: the prompt is a one-shot
 * handoff scoped to this tab, not durable state.
 */

const keyFor = (chatId: string) => `flint-ai-pending-${chatId}`;

/**
 * Stash a first prompt for a brand-new chat and return the minted chat id.
 * The caller then navigates to `/ask/$chatId` with it.
 */
export function stashPendingPrompt(prompt: string): string {
  const chatId = crypto.randomUUID();
  sessionStorage.setItem(keyFor(chatId), prompt);
  return chatId;
}

/**
 * Read-and-clear the pending prompt for a chat, if one was stashed.
 * Returns `null` when the user navigated to the chat directly.
 */
export function takePendingPrompt(chatId: string): string | null {
  const key = keyFor(chatId);
  const pending = sessionStorage.getItem(key);
  if (pending !== null) sessionStorage.removeItem(key);
  return pending;
}
